/**
 * Activation de compte (/c/:token) — clients créés au comptoir de la
 * blanchisserie, sans mot de passe. Réponses de
 * app/controllers/api/v1/claims_controller.rb et
 * app/services/claim_tokens/complete.rb (backend, branche staging).
 *
 *   POST /api/v1/claim/lookup  { token }
 *     200 { status: "valid", first_name, has_dummy_email, email,
 *           orders_count, laundry_place_name }
 *     200 { status: "already_claimed" }
 *     404 { error: "invalid_link" }   (expiré, utilisé ou inconnu)
 *
 *   POST /api/v1/claim  { token, password, email? }
 *     200 comme POST /auth/sign_in
 *     404 invalid_link · 409 already_claimed
 *     409 account_exists + existing_email (masqué : « c***@exemple.be »)
 *     422 email_taken · invalid_email · password_required
 *     422 invalid + message (erreurs du modèle, par champ)
 */

import { isValidEmail } from "./password";

/** État de la page selon la réponse (ou l'échec) du lookup. */
export function lookupState(response, error) {
  if (error) {
    return error?.response?.status === 404 ? { state: "invalid" } : { state: "error" };
  }
  const data = response?.data || {};
  if (data.status === "already_claimed") return { state: "claimed" };
  if (data.status !== "valid") return { state: "error" };
  return {
    state: "form",
    account: {
      firstName: data.first_name || "",
      needsEmail: data.has_dummy_email === true,
      email: data.has_dummy_email ? "" : data.email || "",
      ordersCount: Number(data.orders_count) || 0,
      laundryName: data.laundry_place_name || "",
    },
  };
}

/** Clés de traduction de l'en-tête du formulaire. */
export function greeting({ firstName, ordersCount, laundryName }) {
  const title = firstName ? ["Hello {{name}}", { name: firstName }] : ["Hello", {}];
  const args = { laundry: laundryName, count: ordersCount };
  let lede;
  if (laundryName) {
    lede =
      ordersCount > 1
        ? "Your {{laundry}} account, with {{count}} previous orders."
        : ordersCount === 1
        ? "Your {{laundry}} account, with 1 previous order."
        : "Your {{laundry}} account.";
  } else {
    lede =
      ordersCount > 1
        ? "Your account, with {{count}} previous orders."
        : ordersCount === 1
        ? "Your account, with 1 previous order."
        : "Your account is ready.";
  }
  return { title, lede: [lede, args] };
}

/** Le backend refuse les adresses de remplacement du comptoir. */
const PLACEHOLDER = /@test\.com$/i;

/** Erreurs du formulaire (clés de traduction) avant envoi. */
export function claimFormErrors({ needsEmail, email, password, confirmation }) {
  const value = String(email || "").trim();
  const pass = String(password || "");
  return {
    email: !needsEmail
      ? null
      : !value
      ? "Enter your email address."
      : !isValidEmail(value)
      ? "Enter a valid email address."
      : PLACEHOLDER.test(value)
      ? "Use your own email address."
      : null,
    password: !pass ? "Enter a password." : pass.length < 6 ? "At least 6 characters." : null,
    confirmation: !String(confirmation || "")
      ? "Confirm your password."
      : confirmation !== pass
      ? "The passwords do not match."
      : null,
  };
}

export const hasErrors = (errors) => Object.values(errors).some(Boolean);

/** Corps du POST /claim : pas de champ email quand le compte en a déjà un. */
export function claimBody({ token, needsEmail, email, password }) {
  const body = { token, password };
  if (needsEmail) body.email = String(email || "").trim().toLowerCase();
  return body;
}

/** Premier message d'un tableau d'erreurs Rails, sinon null. */
const first = (list) => (Array.isArray(list) && list.length ? String(list[0]) : null);

/**
 * Suite à donner à un refus du POST /claim :
 *  - { state } : la page change d'écran (invalid, claimed, exists) ;
 *  - { fields } : erreurs sous les champs, le formulaire reste utilisable ;
 *  - { alert } : message au-dessus du bouton (réseau, cas imprévu).
 */
export function claimFailure(error) {
  const status = error?.response?.status;
  const data = error?.response?.data || {};
  const code = data.error;

  if (!error?.response) return { alert: "Network error, please try again later." };
  if (status === 404 || code === "invalid_link") return { state: "invalid" };
  if (code === "already_claimed") return { state: "claimed" };
  if (code === "account_exists") return { state: "exists", existingEmail: data.existing_email || "" };
  if (code === "email_taken") return { fields: { email: "This email address is already used by another account." } };
  if (code === "invalid_email") return { fields: { email: "Enter a valid email address." } };
  if (code === "password_required") return { fields: { password: "Enter a password." } };
  if (code === "invalid" && data.message && typeof data.message === "object") {
    const fields = {};
    const email = first(data.message.email);
    const password = first(data.message.password) || first(data.message.password_confirmation);
    if (email) fields.email = email;
    if (password) fields.password = password;
    if (Object.keys(fields).length) return { fields };
  }
  return { alert: "Something went wrong. Please try again." };
}
